import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import Particle from "../../Components/Particle.js";
import { END_POINT_LOGIN } from "../../Config/index.js";
import { useAuth } from "../../Context/AuthContext";

const inputClass = "w-full px-4 py-3 rounded-xl bg-gray-700 text-white border border-gray-600 focus:outline-none focus:border-blue-400 text-base";

function Login() {
  const [form, setForm] = useState({ emailOrMobile: '', password: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  function validate() {
    const errs = {};
    if (!form.emailOrMobile.trim()) errs.emailOrMobile = 'Email or mobile is required';
    if (!form.password.trim()) errs.password = 'Password is required';
    return errs;
  }

  async function handleLogin(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setApiError('');
    setLoading(true);
    try {
      const payload = { password: form.password };
      const val = form.emailOrMobile.trim();
      if (/^\d{10}$/.test(val)) {
        payload.mobile = val;
      } else {
        payload.email = val;
      }
      const response = await axios.post(END_POINT_LOGIN, payload);
      const { token, user } = response.data;
      login(user, token);
      if (user?.firstTime) {
        navigate('/reset-password');
      } else if (!user?.onboardingComplete) {
        navigate('/onboarding');
      } else {
        navigate('/members');
      }
    } catch (err) {
      setApiError(err?.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  }

  return (
    <section>
      <div className="home-section w-full" id="home">
        <Particle />
        <div className="home-content">
          <div className="flex justify-center px-4">
            <div className="w-full max-w-sm">
              <div className="bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
                <div className="px-6 pt-8 pb-2 text-center">
                  <h2 className="text-2xl font-bold text-white mb-1">Welcome back</h2>
                  <p className="text-gray-400 text-sm">Sign in to your community account</p>
                </div>

                {apiError && (
                  <div className="mx-6 mt-4 px-4 py-3 bg-red-900/40 border border-red-600 rounded-xl text-red-300 text-sm">
                    {apiError}
                  </div>
                )}

                <form onSubmit={handleLogin} className="px-6 py-6 flex flex-col gap-4">
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Email or Mobile</label>
                    <input
                      type="text"
                      name="emailOrMobile"
                      value={form.emailOrMobile}
                      onChange={handleChange}
                      placeholder="Enter email or 10-digit mobile"
                      className={inputClass}
                      autoComplete="username"
                    />
                    {errors.emailOrMobile && <p className="text-red-400 text-xs mt-1">{errors.emailOrMobile}</p>}
                  </div>

                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={form.password}
                        onChange={handleChange}
                        placeholder="Enter your password"
                        className={inputClass + ' pr-8'}
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(p => !p)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 bg-transparent focus:outline-none"
                      >
                        {showPassword ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
                      </button>
                    </div>
                    {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base transition disabled:opacity-60 mt-2"
                  >
                    {loading ? 'Signing in…' : 'Sign In'}
                  </button>
                </form>

                <div className="px-6 pb-8 text-center">
                  <p className="text-gray-400 text-sm">
                    New here?{' '}
                    <Link to="/signup" className="text-blue-400 font-medium hover:underline">
                      Create an account
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Login;
